import { App } from "@capacitor/app";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isAndroid, isNativePlatform } from "@/lib/capacitor";

const STATUS_BAR_COLOR = "#FDFAF7";

async function configureNativeChrome(): Promise<void> {
  document.body.classList.add("capacitor-native");

  // LIGHT = dark icons on the cream app background.
  // setBackgroundColor is a no-op on Android 15+; native windowBackground handles the bar color.
  await StatusBar.setStyle({ style: Style.Light });

  if (isAndroid()) {
    await StatusBar.setBackgroundColor({ color: STATUS_BAR_COLOR });
  }

  await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
  await Keyboard.setScroll({ isDisabled: false });
}

export default function CapacitorApp() {
  const { status } = useAuth();

  useEffect(() => {
    if (!isNativePlatform()) {
      return;
    }

    void configureNativeChrome();
  }, []);

  useEffect(() => {
    if (!isNativePlatform() || status === "loading") {
      return;
    }

    void SplashScreen.hide();
  }, [status]);

  useEffect(() => {
    if (!isNativePlatform()) {
      return;
    }

    const backListener = App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
        return;
      }

      void App.minimizeApp();
    });

    const resumeListener = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        document.body.classList.remove("app-backgrounded");
        void configureNativeChrome();
        return;
      }

      document.body.classList.add("app-backgrounded");
    });

    return () => {
      void backListener.then((handle) => handle.remove());
      void resumeListener.then((handle) => handle.remove());
    };
  }, []);

  return null;
}
